import React, { useMemo, useEffect } from 'react';
import { Box, Paper, Text } from '@mantine/core';
import { useMantineColorScheme, useMantineTheme } from '@mantine/core';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface QuillEditorProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    height?: string;
    disabled?: boolean;
    placeholder?: string;
    name?: string;
}

/**
 * WYSIWYG HTML editor component using React-Quill
 */
export const QuillEditor: React.FC<QuillEditorProps> = ({
    value,
    onChange,
    label,
    height = '400px',
    disabled = false,
    placeholder = 'Enter text here...',
    name = 'quill-editor'
}) => {
    const { colorScheme } = useMantineColorScheme();
    const theme = useMantineTheme();
    const isDark = colorScheme === 'dark';

    useEffect(() => {
        const styleId = `quill-theme-${name}`;
        let styleElement = document.getElementById(styleId) as HTMLStyleElement;

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        const textColor = isDark ? '#C1C2C5' : '#212529';
        const bgColor = isDark ? '#1A1B1E' : '#FFFFFF';
        const toolbarBg = isDark ? '#25262B' : '#F1F3F5';
        const dividerColor = isDark ? '#373A40' : '#DEE2E6';
        const hoverBg = isDark ? '#2C2E33' : '#E9ECEF';
        const selectedBg = isDark ? '#373A40' : '#DEE2E6';
        const secondaryText = isDark ? '#909296' : '#868E96';

        styleElement.textContent = `
            #${name} .ql-toolbar {
                background-color: ${toolbarBg} !important;
                border-bottom: 1px solid ${dividerColor} !important;
                border-top: none !important;
                border-left: none !important;
                border-right: none !important;
            }
            #${name} .ql-toolbar .ql-stroke { stroke: ${textColor} !important; }
            #${name} .ql-toolbar .ql-fill { fill: ${textColor} !important; }
            #${name} .ql-toolbar svg { color: ${textColor} !important; }
            #${name} .ql-toolbar button { color: ${textColor} !important; }
            #${name} .ql-toolbar button:hover { background-color: ${hoverBg} !important; }
            #${name} .ql-toolbar button.ql-active { background-color: ${selectedBg} !important; }
            #${name} .ql-toolbar .ql-picker-label { color: ${textColor} !important; }
            #${name} .ql-toolbar .ql-picker-label::before { color: ${textColor} !important; }
            #${name} .ql-toolbar .ql-picker-options {
                background-color: ${bgColor} !important;
                border: 1px solid ${dividerColor} !important;
            }
            #${name} .ql-toolbar .ql-picker-item { color: ${textColor} !important; }
            #${name} .ql-toolbar .ql-picker-item:hover { background-color: ${hoverBg} !important; }
            #${name} .ql-toolbar .ql-picker-item.ql-selected { background-color: ${selectedBg} !important; }
            #${name} .ql-toolbar path { stroke: ${textColor} !important; fill: ${textColor} !important; }
            #${name} .ql-toolbar line { stroke: ${textColor} !important; }
            #${name} .ql-toolbar polygon { fill: ${textColor} !important; stroke: ${textColor} !important; }
            #${name} .ql-container {
                height: calc(${height} - 42px);
                font-size: 14px;
                background-color: ${bgColor} !important;
                color: ${textColor} !important;
                border: none !important;
                border-top: 1px solid ${dividerColor} !important;
            }
            #${name} .ql-editor {
                min-height: calc(${height} - 42px);
                color: ${textColor} !important;
                background-color: ${bgColor} !important;
            }
            #${name} .ql-editor p, #${name} .ql-editor div,
            #${name} .ql-editor span, #${name} .ql-editor li {
                color: ${textColor} !important;
            }
            #${name} .ql-editor.ql-blank::before {
                color: ${secondaryText} !important;
                font-style: normal !important;
            }
        `;

        return () => {
            const element = document.getElementById(styleId);
            if (element) {
                element.remove();
            }
        };
    }, [colorScheme, isDark, name, height]);

    const modules = useMemo(() => ({
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean']
        ]
    }), []);

    const effectiveModules = useMemo(() => {
        if (disabled) {
            return { toolbar: false };
        }
        return modules;
    }, [disabled, modules]);

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'color', 'background',
        'align',
        'link', 'image'
    ];

    return (
        <Box>
            {label && (
                <Text size="sm" fw={500} mb="xs">
                    {label}
                </Text>
            )}
            <Paper withBorder>
                <ReactQuill
                    theme="snow"
                    value={value}
                    onChange={(v: string) => {
                        if (disabled) return;
                        onChange(v);
                    }}
                    modules={effectiveModules}
                    formats={formats}
                    placeholder={placeholder}
                    readOnly={disabled}
                    id={name}
                />
            </Paper>
        </Box>
    );
};
